import type { FieldConfigResponseShort } from '@/api/field_config';
import { fetchConfigs } from '@/api/field_config';
import { addSectionToSchema as addSectionToSchemaAPI, createSchema as createSchemaAPI, deleteSchema as deleteSchemaAPI, fetchAllSchemas, fetchSectionsBySchema, removeSectionFromSchema as removeSectionFromSchemaAPI, updateSchema as updateSchemaAPI } from '@/api/schemas';
import type { SchemaTranslationWithDetails } from '@/api/schemaTranslations';
import { fetchAllTranslationsWithDetails } from '@/api/schemaTranslations';
import { fetchAllSections } from '@/api/sections';
import { fetchTranslations } from '@/api/translations';
import type { Schema, SchemaUpdate } from '@/api/types/core/schemas';
import type { Section } from '@/api/types/core/sections';
import type { Translation } from '@/api/types/core/translations';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

// Interface pour les schémas avec section_list
export interface SchemaWithSectionList extends Schema {
  section_list: number[];
  created_at: string;
  updated_at: string;
}

// Interface pour les sections étendues avec indentation
export interface DisplaySection extends Section {
  indentationLevel: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

interface SchemaCache {
  schemas: CacheEntry<SchemaWithSectionList[]> | null;
  sections: CacheEntry<DisplaySection[]> | null;
  translations: CacheEntry<Translation[]> | null;
  schemaTranslations: CacheEntry<SchemaTranslationWithDetails[]> | null;
  fieldConfigs: CacheEntry<FieldConfigResponseShort[]> | null;
  schemaSections: Map<string, CacheEntry<number[]>>;
}

export const useSchemasStore = defineStore('schemas', () => {
  // =================== ÉTAT ===================
  const schemas = ref<SchemaWithSectionList[]>([]);
  const sections = ref<DisplaySection[]>([]);
  const translations = ref<Translation[]>([]);
  const schemaTranslations = ref<SchemaTranslationWithDetails[]>([]);
  const fieldConfigs = ref<FieldConfigResponseShort[]>([]);
  
  const loading = ref(false);
  const error = ref<string | null>(null);
  
  // Cache intelligent avec TTL (Time To Live)
  const cache = ref<SchemaCache>({
    schemas: null,
    sections: null,
    translations: null,
    schemaTranslations: null,
    fieldConfigs: null,
    schemaSections: new Map()
  });
  
  const pendingRequests = ref(new Set<string>());
  
  // Configuration du cache (en millisecondes)
  const CACHE_TTL = {
    SCHEMAS: 5 * 60 * 1000,      // 5 minutes
    SECTIONS: 10 * 60 * 1000,    // 10 minutes (changent moins souvent)
    TRANSLATIONS: 10 * 60 * 1000, // 10 minutes
    CONFIGS: 5 * 60 * 1000,      // 5 minutes
    SCHEMA_SECTIONS: 2 * 60 * 1000 // 2 minutes (changent plus souvent)
  };

  // =================== UTILITAIRES CACHE ===================
  const isExpired = (entry: CacheEntry<unknown> | null): boolean => {
    if (!entry) return true;
    return Date.now() > entry.expiry;
  };

  const createCacheEntry = <T>(data: T, ttl: number): CacheEntry<T> => ({
    data,
    timestamp: Date.now(),
    expiry: Date.now() + ttl
  });

  const clearExpiredCache = (): void => {
    const now = Date.now();
    
    if (cache.value.schemas && now > cache.value.schemas.expiry) {
      cache.value.schemas = null;
    }
    if (cache.value.sections && now > cache.value.sections.expiry) {
      cache.value.sections = null;
    }
    if (cache.value.translations && now > cache.value.translations.expiry) {
      cache.value.translations = null;
    }
    if (cache.value.schemaTranslations && now > cache.value.schemaTranslations.expiry) {
      cache.value.schemaTranslations = null;
    }
    if (cache.value.fieldConfigs && now > cache.value.fieldConfigs.expiry) {
      cache.value.fieldConfigs = null;
    }
    
    // Nettoyer les sections de schémas expirées
    for (const [key, entry] of cache.value.schemaSections.entries()) {
      if (now > entry.expiry) {
        cache.value.schemaSections.delete(key);
      }
    }
  };

  // =================== COMPUTED ===================
  const availableSchemaTypes = computed(() => {
    const types = new Set(schemas.value.map(s => s.type));
    return Array.from(types).sort();
  });

  const organizedSections = computed(() => {
    return organizeByHierarchyAndOrder(sections.value);
  });

  // =================== ACTIONS CORE ===================
  
  /**
   * 🚀 FONCTION PRINCIPALE OPTIMISÉE
   * Charge toutes les données nécessaires avec un minimum de requêtes
   */
  const initializeSchemasData = async (forceRefresh = false): Promise<void> => {
    const requestKey = 'initializeSchemasData';
    
    // Éviter les requêtes multiples simultanées
    if (pendingRequests.value.has(requestKey)) {
      return;
    }

    clearExpiredCache();

    pendingRequests.value.add(requestKey);
    loading.value = true;
    error.value = null;

    try {
      // ===== ÉTAPE 1: Charger les données de base en parallèle =====
      const promises: Array<Promise<unknown>> = [];

      // Sections (si pas en cache ou force refresh)
      if (forceRefresh || isExpired(cache.value.sections)) {
        promises.push(fetchSectionsData());
      } else {
        sections.value = cache.value.sections!.data;
      }

      // Schémas de base
      if (forceRefresh || isExpired(cache.value.schemas)) {
        promises.push(fetchSchemasBaseData());
      } else {
        schemas.value = cache.value.schemas!.data;
      }

      // Traductions globales
      if (forceRefresh || isExpired(cache.value.translations)) {
        promises.push(fetchTranslationsData());
      } else {
        translations.value = cache.value.translations!.data;
      }

      // Traductions schema-spécifiques
      if (forceRefresh || isExpired(cache.value.schemaTranslations)) {
        promises.push(fetchSchemaTranslationsData());
      } else {
        schemaTranslations.value = cache.value.schemaTranslations!.data;
      }

      // Configurations de champs
      if (forceRefresh || isExpired(cache.value.fieldConfigs)) {
        promises.push(fetchFieldConfigsData());
      } else {
        fieldConfigs.value = cache.value.fieldConfigs!.data;
      }

      // Attendre que les données de base soient chargées
      await Promise.all(promises);

      // ===== ÉTAPE 2: Charger les sections des schémas =====
      await fetchAllSchemaSections(forceRefresh);

      console.log(`✅ Données des schémas initialisées avec succès`);
      console.log(`📊 Statistiques: ${schemas.value.length} schémas, ${sections.value.length} sections, ${translations.value.length} traductions`);

    } catch (err) {
      console.error('❌ Erreur lors de l\'initialisation des données schémas:', err);
      error.value = 'Erreur lors du chargement des données';
      throw err;
    } finally {
      loading.value = false;
      pendingRequests.value.delete(requestKey);
    }
  };

  // =================== FONCTIONS DE FETCH OPTIMISÉES ===================

  const fetchSchemasBaseData = async (): Promise<void> => {
    console.log('🔄 Récupération des schémas...');
    const rawSchemas = await fetchAllSchemas();
    
    const schemasWithSectionList: SchemaWithSectionList[] = rawSchemas.map((schema: Schema) => ({
      ...schema,
      section_list: [], // Sera rempli par fetchAllSchemaSections
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));

    schemas.value = schemasWithSectionList;
    cache.value.schemas = createCacheEntry(schemasWithSectionList, CACHE_TTL.SCHEMAS);
  };

  const fetchSectionsData = async (): Promise<void> => {
    console.log('🔄 Récupération des sections...');
    const rawSections = await fetchAllSections();
    
    const sectionsWithDisplay: DisplaySection[] = rawSections.map((section: Section) => ({
      id: section.id,
      name: section.name,
      type: section.type,
      display: section.display,
      mandatory_entry: section.mandatory_entry,
      ordre: section.ordre,
      importance: section.importance,
      parent_id: section.parent_id,
      subsections: [],
      indentationLevel: getDepthLevel(section.name)
    }));

    sections.value = sectionsWithDisplay;
    cache.value.sections = createCacheEntry(sectionsWithDisplay, CACHE_TTL.SECTIONS);
  };

  const fetchTranslationsData = async (): Promise<void> => {
    console.log('🔄 Récupération des traductions...');
    const translationsData = await fetchTranslations();
    translations.value = translationsData;
    cache.value.translations = createCacheEntry(translationsData, CACHE_TTL.TRANSLATIONS);
  };

  const fetchSchemaTranslationsData = async (): Promise<void> => {
    console.log('🔄 Récupération des traductions schema-spécifiques...');
    const schemaTranslationsData = await fetchAllTranslationsWithDetails();
    schemaTranslations.value = schemaTranslationsData;
    cache.value.schemaTranslations = createCacheEntry(schemaTranslationsData, CACHE_TTL.TRANSLATIONS);
  };

  const fetchFieldConfigsData = async (): Promise<void> => {
    console.log('🔄 Récupération des configurations de champs...');
    const configsData = await fetchConfigs();
    fieldConfigs.value = configsData;
    cache.value.fieldConfigs = createCacheEntry(configsData, CACHE_TTL.CONFIGS);
  };

  /**
   * 🚀 OPTIMISATION CLÉE: Récupération groupée des sections de schémas
   * Au lieu d'une requête par schéma, on fait une seule requête optimisée
   */
  const fetchAllSchemaSections = async (forceRefresh = false): Promise<void> => {
    console.log('🔄 Récupération optimisée des sections de schémas...');
    
    // Identifier les schémas qui ont besoin de leurs sections
    const schemasNeedingFetch = forceRefresh 
      ? schemas.value 
      : schemas.value.filter(schema => {
          const cacheEntry = cache.value.schemaSections.get(schema.uuid);
          return isExpired(cacheEntry || null);
        });

    if (schemasNeedingFetch.length === 0) {
      // Récupérer depuis le cache
      for (const schema of schemas.value) {
        const cacheEntry = cache.value.schemaSections.get(schema.uuid);
        if (cacheEntry) {
          schema.section_list = cacheEntry.data;
        }
      }
      return;
    }

    // 💡 OPTIMISATION: Au lieu de N requêtes, on fait N requêtes en parallèle
    const sectionPromises = schemasNeedingFetch.map(async (schema) => {
      try {
        const schemaSections = await fetchSectionsBySchema(schema.uuid);
        const sectionIds = schemaSections.map((s: Section) => s.id);
        
        // Mettre à jour le schéma
        schema.section_list = sectionIds;
        
        // Mettre en cache
        cache.value.schemaSections.set(
          schema.uuid, 
          createCacheEntry(sectionIds, CACHE_TTL.SCHEMA_SECTIONS)
        );

        return { schemaUuid: schema.uuid, sectionIds };
      } catch (error) {
        console.warn(`⚠️ Impossible de récupérer les sections pour le schéma ${schema.uuid}:`, error);
        schema.section_list = [];
        return { schemaUuid: schema.uuid, sectionIds: [] };
      }
    });

    // Exécuter toutes les requêtes en parallèle
    const results = await Promise.allSettled(sectionPromises);
    
    const successCount = results.filter(r => r.status === 'fulfilled').length;
    const errorCount = results.filter(r => r.status === 'rejected').length;
    
    console.log(`✅ Sections de schémas récupérées: ${successCount} succès, ${errorCount} erreurs`);
  };

  // =================== FONCTIONS UTILITAIRES ===================

  const organizeByHierarchyAndOrder = (sections: DisplaySection[]): DisplaySection[] => {
    const result: DisplaySection[] = [];
    const processed = new Set<string>();
    
    const addSectionAndChildren = (parentPath: string): void => {
      const children = sections.filter(s => {
        const parentPrefix = parentPath ? parentPath + '.' : '';
        const isDirectChild = parentPath === '' 
          ? !s.name.includes('.')
          : s.name.startsWith(parentPrefix) && 
            !s.name.substring(parentPrefix.length).includes('.');
        
        return isDirectChild && !processed.has(s.name);
      });
      
      children.sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
      
      children.forEach(child => {
        if (!processed.has(child.name)) {
          result.push(child);
          processed.add(child.name);
          addSectionAndChildren(child.name);
        }
      });
    };
    
    addSectionAndChildren('');
    return result;
  };

  const getDepthLevel = (sectionName: string): number => {
    return sectionName.split('.').length - 1;
  };

  // =================== ACTIONS CRUD ===================

  const createSchema = async (schemaData: { appellation: string; type: string; description: string }): Promise<SchemaWithSectionList> => {
    const createData = {
      appellation: schemaData.appellation,
      type: schemaData.type,
      description: schemaData.description,
      section_list: []
    };
    const newSchema = await createSchemaAPI(createData);
    const schemaWithSectionList: SchemaWithSectionList = {
      ...newSchema,
      section_list: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    schemas.value.push(schemaWithSectionList);
    
    // Invalider le cache des schémas
    cache.value.schemas = null;
    
    return schemaWithSectionList;
  };

  const updateSchema = async (uuid: string, data: SchemaUpdate): Promise<Schema> => {
    const updatedSchema = await updateSchemaAPI(uuid, data);
    
    const index = schemas.value.findIndex(s => s.uuid === uuid);
    if (index !== -1) {
      schemas.value[index] = { ...schemas.value[index], ...updatedSchema };
    }
    
    // Invalider le cache des schémas
    cache.value.schemas = null;
    
    return updatedSchema;
  };

  const deleteSchema = async (uuid: string): Promise<void> => {
    await deleteSchemaAPI(uuid);
    
    const index = schemas.value.findIndex(s => s.uuid === uuid);
    if (index !== -1) {
      schemas.value.splice(index, 1);
    }
    
    // Nettoyer le cache
    cache.value.schemas = null;
    cache.value.schemaSections.delete(uuid);
  };

  const addSectionToSchema = async (schemaUuid: string, sectionId: number): Promise<void> => {
    await addSectionToSchemaAPI(schemaUuid, sectionId);
    
    const schema = schemas.value.find(s => s.uuid === schemaUuid);
    if (schema && !schema.section_list.includes(sectionId)) {
      schema.section_list.push(sectionId);
      
      // Invalider le cache pour ce schéma
      cache.value.schemaSections.delete(schemaUuid);
    }
  };

  const removeSectionFromSchema = async (schemaUuid: string, sectionId: number): Promise<void> => {
    await removeSectionFromSchemaAPI(schemaUuid, sectionId);
    
    const schema = schemas.value.find(s => s.uuid === schemaUuid);
    if (schema) {
      const index = schema.section_list.indexOf(sectionId);
      if (index > -1) {
        schema.section_list.splice(index, 1);
        
        // Invalider le cache pour ce schéma
        cache.value.schemaSections.delete(schemaUuid);
      }
    }
  };

  // =================== ACTIONS CACHE ===================

  const clearCache = (): void => {
    cache.value = {
      schemas: null,
      sections: null,
      translations: null,
      schemaTranslations: null,
      fieldConfigs: null,
      schemaSections: new Map()
    };
  };

  // =================== ACTIONS OPTIMISÉES ===================
  
  const refreshSchemaTranslations = async (): Promise<void> => {
    console.log('🔄 Rechargement optimisé des traductions schéma...');
    try {
      await fetchSchemaTranslationsData();
      console.log('✅ Traductions schéma rechargées avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du rechargement des traductions schéma:', error);
      throw error;
    }
  };

  const getCacheStats = () => {
    const now = Date.now();
    return {
      schemas: cache.value.schemas ? {
        cached: true,
        age: now - cache.value.schemas.timestamp,
        ttl: cache.value.schemas.expiry - now,
        expired: isExpired(cache.value.schemas)
      } : { cached: false },
      sections: cache.value.sections ? {
        cached: true,
        age: now - cache.value.sections.timestamp,
        ttl: cache.value.sections.expiry - now,
        expired: isExpired(cache.value.sections)
      } : { cached: false },
      schemaSections: {
        count: cache.value.schemaSections.size,
        entries: Array.from(cache.value.schemaSections.entries()).map(([key, entry]) => ({
          schemaUuid: key,
          age: now - entry.timestamp,
          ttl: entry.expiry - now,
          expired: isExpired(entry)
        }))
      }
    };
  };

  // =================== RETURN ===================
  return {
    // État
    schemas,
    sections,
    translations,
    schemaTranslations,
    fieldConfigs,
    loading,
    error,
    
    // Computed
    availableSchemaTypes,
    organizedSections,
    
    // Actions principales
    initializeSchemasData,
    refreshSchemaTranslations,
    
    // Actions CRUD
    createSchema,
    updateSchema,
    deleteSchema,
    addSectionToSchema,
    removeSectionFromSchema,
    
    // Actions cache
    clearCache,
    getCacheStats,
    
    // Utilitaires
    organizeByHierarchyAndOrder
  };
}); 